<template>
  <div class="privacy-container">
    <h1 class="title">PRIVACY NOTICE</h1>
    <p class="subtitle">Last updated: March 24, 2025</p>

    <div class="intro-section">
      <p>This Privacy Notice for <strong>ACM India</strong> ("we," "us," or "our") describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:</p>
      <p>Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services.</p>
    </div>

    <section class="summary-section">
      <h2>SUMMARY OF KEY POINTS</h2>
      <p>This summary provides key points from our Privacy Notice. You can find more details about any of these topics by clicking the link following each key point or using our table of contents below.</p>

      <ul class="key-points">
        <li><strong>What personal information do we process?</strong> <a href="#personal-information">Learn more</a>.</li>
        <li><strong>Do we process any sensitive personal information?</strong> No.</li>
        <li><strong>Do we collect any information from third parties?</strong> <a href="#third-party-information">Learn more</a>.</li>
        <li><strong>How do we process your information?</strong> <a href="#processing-information">Learn more</a>.</li>
        <li><strong>With whom do we share personal information?</strong> <a href="#sharing-information">Learn more</a>.</li>
        <li><strong>What are your rights?</strong> <a href="#privacy-rights">Learn more</a>.</li>
        <li><strong>How do you exercise your rights?</strong> <a href="#exercise-rights">Learn more</a>.</li>
      </ul>
    </section>

    <section class="toc-section">
      <h2>TABLE OF CONTENTS</h2>
      <ol>
        <li><a href="#personal-information">WHAT INFORMATION DO WE COLLECT?</a></li>
        <li><a href="#processing-information">HOW DO WE PROCESS YOUR INFORMATION?</a></li>
        <li><a href="#sharing-information">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a></li>
        <li><a href="#cookies-tracking">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</a></li>
        <li><a href="#social-logins">HOW DO WE HANDLE YOUR SOCIAL LOGINS?</a></li>
        <li><a href="#international-transfers">IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</a></li>
        <li><a href="#data-retention">HOW LONG DO WE KEEP YOUR INFORMATION?</a></li>
        <li><a href="#minors">DO WE COLLECT INFORMATION FROM MINORS?</a></li>
        <li><a href="#privacy-rights">WHAT ARE YOUR PRIVACY RIGHTS?</a></li>
        <li><a href="#dnt-controls">CONTROLS FOR DO-NOT-TRACK FEATURES</a></li>
        <li><a href="#updates">DO WE MAKE UPDATES TO THIS NOTICE?</a></li>
        <li><a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a></li>
        <li><a href="#review-update-delete">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT?</a></li>
      </ol>
    </section>

    <section id="personal-information">
      <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
      <h3>Personal information you disclose to us</h3>
      <p>We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information, participate in activities, or contact us.</p>
    </section>

    <section id="processing-information">
      <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
      <p>We process your personal information to provide, improve, and administer our Services, communicate with you, ensure security and fraud prevention, and comply with the law.</p>
    </section>

    <section id="sharing-information">
      <h2>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
      <ul>
        <li><strong>Business Transfers:</strong> Information may be shared during mergers, acquisitions, or company sales.</li>
        <li><strong>Affiliates:</strong> We share information with our parent company, subsidiaries, and affiliates.</li>
        <li><strong>Business Partners:</strong> We may share information for products, services, or promotions.</li>
      </ul>
    </section>

    <section id="cookies-tracking">
      <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
      <p>Yes, we use cookies and similar tracking technologies to collect and store information.</p>
    </section>

    <section id="social-logins">
      <h2>5. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h2>
      <p>Our Services allow login via third-party social media accounts (e.g., Facebook, X). The profile information we receive depends on your social media provider.</p>
    </section>

    <section id="privacy-rights">
      <h2>9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
      <ul>
        <li><strong>Withdraw consent:</strong> You can withdraw consent at any time.</li>
        <li><strong>Account changes:</strong> You can review, update, or delete your account information.</li>
      </ul>
    </section>

    <section id="updates">
      <h2>11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
      <p>Yes, we may update this notice periodically to stay compliant with relevant laws.</p>
    </section>

    <section id="contact" class="contact-section">
      <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
      <p>If you have questions, contact us at:</p>
      <div class="contact-info">
        <p><strong>ACM India</strong></p>
        <p>India</p>
        <p>Email: contact@india.acm.org</p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.privacy-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.title {
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.2rem;
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
}

h2 {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 2rem 0 1rem;
}

h3 {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 1.5rem 0 1rem;
}

p {
  margin-bottom: 1rem;
  line-height: 1.6;
}

.key-points {
  list-style: disc;
  margin: 1rem 0;
  padding-left: 2rem;
}

.key-points li {
  margin-bottom: 0.5rem;
}

section {
  margin: 3rem 0;
}

.contact-info {
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 4px;
  margin-top: 1rem;
}

a {
  color: #3273dc;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.intro-section,
.summary-section,
.toc-section {
  margin-bottom: 2rem;
}

ol {
  list-style: decimal;
  margin: 1rem 0;
  padding-left: 2rem;
}

ol li {
  margin-bottom: 0.5rem;
}
</style>
